import React, { useEffect, useMemo, useState } from "react";
import AuditClient from "../../services/dashboard/AuditClient";
import toast from "react-hot-toast";
import { Search, ChevronLeft, ChevronRight, ArrowUpDown, ChevronDown, ChevronUp } from "lucide-react";

/* ------------------- helpers ------------------- */

function safeParseJson(maybeJson) {
  if (!maybeJson) return null;
  if (typeof maybeJson === "object") return maybeJson;
  try {
    return JSON.parse(maybeJson);
  } catch {
    return null;
  }
}

function getBefore(obj) {
  return safeParseJson(obj?.beforeState || obj?.before_state || obj?.before || null);
}
function getAfter(obj) {
  return safeParseJson(obj?.afterState || obj?.after_state || obj?.after || null);
}

function formatDate(value) {
  if (!value) return "-";
  try {
    let date;
    if (typeof value === "number") {
      date = new Date(Math.floor(value * 1000));
    } else if (Array.isArray(value)) {
      const [y, m, d, hh = 0, mm = 0, ss = 0] = value;
      date = new Date(y, (m ? m - 1 : 0), d || 1, hh, mm, ss);
    } else {
      date = new Date(value);
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return String(value);
  }
}

function prettyKey(k = "") {
  return k
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/^./, (s) => s.toUpperCase());
}

function capitalize(s = "") {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function buildSummaryFromLog(l) {
  const entityType = (l.entityType || l.entity_type || "").toLowerCase();
  const before = getBefore(l);
  const after = getAfter(l);

  const pick = (o) => (o && typeof o === "object" ? o : null);

  const pickBook = pick(after) || pick(before);
  const pickAuthor = pick(after?.author) || pick(before?.author) || (pickBook && pickBook.author) || null;
  const pickCategory = pick(after?.category) || pick(before?.category) || (pickBook && pickBook.category) || null;

  const getName = (o, keys = ["name", "title", "bookName", "authorName", "catName", "planName", "packageName", "offerTitle", "couponCode"]) => {
    if (!o) return null;
    for (const k of keys) {
      if (o[k]) return o[k];
    }
    return null;
  };

  const objToKv = (obj) => {
    if (!obj || typeof obj !== "object") return [];
    return Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => ({
        key: prettyKey(k),
        value: (typeof v === "object" ? (v.name ?? JSON.stringify(v)) : String(v)).slice(0, 120),
        fullValue: typeof v === "object" ? JSON.stringify(v) : String(v),
      }));
  };

  if (entityType.includes("book")) {
    const name = getName(pickBook, ["bookName", "title", "name"]);
    const authorName = getName(pickAuthor, ["authorName", "name"]) || (pickBook && (pickBook.authorName || pickBook.author?.authorName || pickBook.author?.name));
    const catName = getName(pickCategory, ["catName", "name"]) || (pickBook && (pickBook.categoryName || pickBook.category?.catName || pickBook.category?.name));
    const previewParts = [name && `Book: ${name}`, authorName && `Author: ${authorName}`, catName && `Category: ${catName}`].filter(Boolean);
    const preview = previewParts.join(" — ") || (l.details || "-");
    const details = after ? objToKv(after) : before ? objToKv(before) : [];
    return { preview, details };
  }

  if (entityType.includes("author")) {
    const name = getName(after) || getName(before);
    const preview = name ? `Author: ${name}` : (l.details || "-");
    const details = after ? objToKv(after) : before ? objToKv(before) : [];
    return { preview, details };
  }

  if (entityType.includes("admin")) {
    const name = getName(after) || getName(before) || after?.username || before?.username;
    const roles = (after && after.roles) || (before && before.roles) || [];
    const rolesStr = Array.isArray(roles) ? roles.slice(0,3).map(r => (typeof r === "string" ? r : r.name || r.code)).join(", ") : "";
    const preview = [name && `Admin: ${name}`, rolesStr && `Roles: ${rolesStr}`].filter(Boolean).join(" — ") || (l.details || "-");
    const details = after ? objToKv(after) : before ? objToKv(before) : [];
    return { preview, details };
  }

  if (entityType.includes("package") || entityType.includes("plan")) {
    const name = getName(after) || getName(before) || (after && (after.planName || after.packageName)) || (before && (before.planName || before.packageName));
    const preview = name ? `${capitalize(entityType)}: ${name}` : (l.details || "-");
    const details = after ? objToKv(after) : before ? objToKv(before) : [];
    return { preview, details };
  }

  if (entityType.includes("offer")) {
    const title = getName(after, ["offerTitle", "title"]) || getName(before, ["offerTitle", "title"]);
    const preview = title ? `Offer: ${title}` : (l.details || "-");
    const details = after ? objToKv(after) : before ? objToKv(before) : [];
    return { preview, details };
  }

  if (entityType.includes("coupon")) {
    const code = getName(after, ["couponCode", "code"]) || getName(before, ["couponCode", "code"]);
    const preview = code ? `Coupon: ${code}` : (l.details || "-");
    const details = after ? objToKv(after) : before ? objToKv(before) : [];
    return { preview, details };
  }

  if (entityType.includes("order") || entityType.includes("bookorder")) {
    const orderId =
      (l.entityId || l.entity_id) ||
      (after && (after.orderId || after.bookOrderId)) ||
      (before && (before.orderId || before.bookOrderId));
    const bookName = getName(after, ["bookTitle","bookName"]) || getName(before, ["bookTitle","bookName"]);
    const userName = (after && (after.username || (after.user && after.user.username))) || (before && (before.username || (before.user && before.user.username)));
    const previewParts = [orderId && `Order: ${orderId}`, bookName && `Book: ${bookName}`, userName && `User: ${userName}`].filter(Boolean);
    const preview = previewParts.join(" — ") || (l.details || "-");
    const details = after ? objToKv(after) : before ? objToKv(before) : [];
    return { preview, details };
  }

  const fallbackName = (after && (after.name || after.title)) || (before && (before.name || before.title));
  const fallbackPreview = fallbackName || l.details || "-";
  const fallbackDetails = after ? objToKv(after) : before ? objToKv(before) : [];
  return { preview: fallbackPreview, details: fallbackDetails };
}

/* ------------------- component ------------------- */

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(25);
  const [query, setQuery] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [entities, setEntities] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showCreators, setShowCreators] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
   const [summaryLoading, setSummaryLoading] = useState(false);
  const [totalBookCreates, setTotalBookCreates] = useState(0);
  const [creatorCountsSorted, setCreatorCountsSorted] = useState([]);

// 🧭 Fetch logs when page changes
useEffect(() => {
  fetchLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [page]);

// 🧭 Fetch summary only once on mount
useEffect(() => {
  fetchSummary();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// ✅ Safe summary fetch + normalization
async function fetchSummary() {
  setSummaryLoading(true);
  try {
    const res = await AuditClient.getBookCreationSummary();
    const data = res?.data?.responseData || res?.data || res;

    const total = Number(data?.totalBookCreates ?? data?.total ?? 0);
    const rawCreators = data?.creatorCounts ?? data?.creators ?? [];

    const normalized = (Array.isArray(rawCreators) ? rawCreators : []).map((c) => ({
      admin: c?.admin ?? c?.username ?? c?.name ?? "Unknown",
      count: Number(c?.count ?? c?.cnt ?? 0),
    })).sort((a, b) => b.count - a.count);

    console.log("✅ Book Summary:", { total, normalized }); // debugging check

    setTotalBookCreates(total);
    setCreatorCountsSorted(normalized);
  } catch (err) {
    console.error("❌ Failed to fetch summary", err);

    // fallback: derive page-limited counts
    const map = new Map();
    let total = 0;
    for (const l of logs) {
      const action = (l.action || "").toUpperCase();
      const entityType = (l.entityType || l.entity_type || "").toLowerCase();
      if (action === "CREATE" && entityType.includes("book")) {
        total++;
        const adminName =
          l.adminUsername ||
          l.admin_username ||
          (l.admin && (l.admin.username || l.admin.name)) ||
          "System";
        map.set(adminName, (map.get(adminName) || 0) + 1);
      }
    }
    const arr = Array.from(map.entries())
      .map(([admin, count]) => ({ admin, count }))
      .sort((a, b) => b.count - a.count);
    setTotalBookCreates(total);
    setCreatorCountsSorted(arr);
  } finally {
    setSummaryLoading(false);
  }
}


  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function fetchLogs() {
    setLoading(true);
    try {
      const res = await AuditClient.getAll({ page, size });
      const data = res.data && (res.data.responseData || res.data) ? (res.data.responseData || res.data) : res;
      const list = data && data.content ? data.content : (Array.isArray(data) ? data : []);
      setLogs(list || []);
      setTotalPages(data?.totalPages || 1);
      setEntities(Array.from(new Set((list || []).map((l) => (l.entityType || l.entity_type || "Unknown")))).filter(Boolean));
    } catch (e) {
      console.error(e);
      toast.error("Failed to load activity logs");
    } finally {
      setLoading(false);
    }
  }

  function handleSort(key) {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  }

  const filteredAndSorted = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    const filtered = logs.filter((l) => {
      if (entityFilter && (l.entityType || l.entity_type) !== entityFilter) return false;
      const fields = [
        l.adminUsername || l.admin_username || (l.admin && l.admin.username) || "",
        l.action || "",
        l.entityType || l.entity_type || "",
        l.entityId || l.entity_id || "",
        l.details || "",
        l.beforeState || l.before_state || "",
        l.afterState || l.after_state || "",
      ].join(" ").toLowerCase();
      return !q || fields.includes(q);
    });

    const key = sortConfig.key;
    const dir = sortConfig.direction === "asc" ? 1 : -1;

    const normalize = (v) => {
      if (v === null || v === undefined) return "";
      if (typeof v === "number") return v;
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
      return String(v).toLowerCase();
    };

    return filtered.slice().sort((a, b) => {
      if (key === "createdAt") {
        const aTime = a.createdAt || a.created_at || a.createdAtTimestamp || 0;
        const bTime = b.createdAt || b.created_at || b.createdAtTimestamp || 0;
        const A = normalize(aTime);
        const B = normalize(bTime);
        if (A > B) return dir;
        if (A < B) return -dir;
        return 0;
      }
      const aVal = a[key] || a[key.toLowerCase()] || "";
      const bVal = b[key] || b[key.toLowerCase()] || "";
      const A = normalize(aVal);
      const B = normalize(bVal);
      if (A > B) return dir;
      if (A < B) return -dir;
      return 0;
    });
  }, [logs, query, entityFilter, sortConfig]);



  const getSortIcon = (key) =>
    sortConfig.key === key ? (
      <ArrowUpDown size={14} className="inline ml-1 opacity-80" />
    ) : (
      <ArrowUpDown size={14} className="inline ml-1 opacity-40" />
    );

  const toggleRowExpansion = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) newExpanded.delete(id);
    else newExpanded.add(id);
    setExpandedRows(newExpanded);
  };

  // Helper to get initials for avatar
  function getInitials(name) {
    if (!name) return "SY";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // Helper to get action badge styling
  function getActionBadgeStyle(action) {
    const actionUpper = (action || "").toString().toUpperCase();
    if (actionUpper === "CREATE") {
      return "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-700/50";
    }
    if (actionUpper === "UPDATE") {
      return "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200/80 dark:border-amber-700/50";
    }
    return "bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border border-rose-200/80 dark:border-rose-700/50";
  }

  return (
    <div className="p-6 font-nunito bg-gray-50 dark:bg-gray-900 min-h-screen space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Activity Log</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">All admin activities recorded with timestamps.</p>
      </div>

      {/* Summary Cards */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-3 border border-gray-200/80 dark:border-gray-700/80 shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
            {summaryLoading ? "Loading..." : "Total Activities"}
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {summaryLoading ? "..." : totalBookCreates / 2}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Book creations</div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-3 border border-gray-200/80 dark:border-gray-700/80 shadow-sm flex-1 min-w-[320px] max-w-[600px]">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
              Top Creators
            </div>
            {creatorCountsSorted.length > 5 && (
              <button
                onClick={() => setShowCreators((s) => !s)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
              >
                {showCreators ? "Show less" : "Show all"}
              </button>
            )}
          </div>

          <div className="flex gap-1.5 flex-wrap">
            {creatorCountsSorted.slice(0, showCreators ? undefined : 5).map((c) => (
              <button
                key={c.admin}
                onClick={() => setQuery(c.admin)}
                className="px-2.5 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-200/80 dark:border-blue-700/50 shadow-sm transition-all duration-150 hover:shadow"
                title={`Filter logs by ${c.admin}`}
              >
                {c.admin} <span className="text-blue-600 dark:text-blue-400">({c.count / 2})</span>
              </button>
            ))}
            {creatorCountsSorted.length === 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400">No creators yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search activity..."
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200"
          />
        </div>

        <select
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
          className="py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all duration-200"
        >
          <option value="">All Entities</option>
          {entities.map((en) => (
            <option key={en} value={en}>
              {en}
            </option>
          ))}
        </select>

        <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
          {loading ? "Loading..." : `${filteredAndSorted.length} shown`}
        </div>
      </div>

      {/* Data List (Card-Style Table) */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-200/80 dark:border-gray-700/80 bg-gray-50/50 dark:bg-gray-900/50">
                <th className="px-4 py-3">
                  <button
                    onClick={() => handleSort("createdAt")}
                    className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors flex items-center gap-1"
                  >
                    Time {getSortIcon("createdAt")}
                  </button>
                </th>
                <th className="px-4 py-3">
                  <button
                    onClick={() => handleSort("adminUsername")}
                    className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors flex items-center gap-1"
                  >
                    Admin {getSortIcon("adminUsername")}
                  </button>
                </th>
                <th className="px-4 py-3">
                  <button
                    onClick={() => handleSort("action")}
                    className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors flex items-center gap-1"
                  >
                    Action {getSortIcon("action")}
                  </button>
                </th>
                <th className="px-4 py-3">
                  <button
                    onClick={() => handleSort("entityType")}
                    className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors flex items-center gap-1"
                  >
                    Entity {getSortIcon("entityType")}
                  </button>
                </th>
                <th className="px-4 py-3">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                    Entity ID
                  </span>
                </th>
                <th className="px-4 py-3">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                    Summary
                  </span>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-4 py-3.5"><div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                    <td className="px-4 py-3.5"><div className="h-9 w-32 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                    <td className="px-4 py-3.5"><div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" /></td>
                    <td className="px-4 py-3.5"><div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                    <td className="px-4 py-3.5"><div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                    <td className="px-4 py-3.5"><div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                  </tr>
                ))
              ) : filteredAndSorted.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <Search size={40} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No activity found.</p>
                  </td>
                </tr>
              ) : (
                filteredAndSorted.map((l) => {
                  const created = formatDate(l.createdAt || l.created_at);
                  const isExpanded = expandedRows.has(l.id);
                  const summaryObj = buildSummaryFromLog(l);
                  const preview = summaryObj.preview || "-";
                  const details = summaryObj.details || [];
                  const adminName = l.adminUsername || l.admin_username || (l.admin && l.admin.username) || "System";

                  return (
                    <React.Fragment key={l.id}>
                      <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-all duration-150">
                        <td className="px-4 py-3.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400">{created}</span>
                        </td>

                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/50 dark:to-purple-800/50 text-purple-700 dark:text-purple-300 flex items-center justify-center font-semibold shadow-sm">
                              <span className="text-[10px]">{getInitials(adminName)}</span>
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{adminName}</span>
                          </div>
                        </td>

                        <td className="px-4 py-3.5">
                          <span className={`px-2.5 py-1 text-[11px] font-medium rounded-full shadow-sm ${getActionBadgeStyle(l.action)}`}>
                            {l.action}
                          </span>
                        </td>

                        <td className="px-4 py-3.5">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{l.entityType || l.entity_type}</span>
                        </td>

                        <td className="px-4 py-3.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                            {l.entityId || l.entity_id ? `#${l.entityId || l.entity_id}` : "-"}
                          </span>
                        </td>

                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-700 dark:text-gray-300 max-w-[320px] truncate" title={preview}>
                              {preview}
                            </span>
                            {details.length > 0 && (
                              <button
                                onClick={() => toggleRowExpansion(l.id)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 dark:hover:text-blue-400 transition-all duration-150"
                                title={isExpanded ? "Collapse details" : "Expand details"}
                              >
                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {isExpanded && details.length > 0 && (
                        <tr className="bg-gray-50/50 dark:bg-gray-900/50">
                          <td colSpan={6} className="px-4 py-4">
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200/80 dark:border-gray-700/80">
                              <h4 className="text-xs uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400 mb-3">
                                {l.action === "CREATE" ? "Created Data" : l.action === "DELETE" ? "Deleted Data" : "Changed Fields"}
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {details.map((item, idx) => (
                                  <div key={idx} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200/80 dark:border-gray-600/50">
                                    <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                                      {item.key}
                                    </div>
                                    <div className="text-sm text-gray-900 dark:text-gray-200 mt-1.5 break-words">
                                      {item.fullValue}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Page <span className="font-medium text-gray-900 dark:text-white">{page + 1}</span> of{" "}
          <span className="font-medium text-gray-900 dark:text-white">{totalPages}</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-150"
            title="Previous page"
          >
            <ChevronLeft size={18} />
          </button>

          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-150"
            title="Next page"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
